import React, { useState } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff, LockKeyhole, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { saveAdminSession } from "@/lib/adminAuth";
import { newsletterApi } from "@/lib/newsletterApi";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    password: "",
  });
  const [statusMessage, setStatusMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSubmitting(true);
      const data = await newsletterApi.loginAdmin(form);
      saveAdminSession(data);
      navigate("/admin/newsletter");
    } catch (error) {
      setStatusMessage(error.response?.data?.message || "Unable to login.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar onContactClick={() => window.location.assign("/")} />

      <section className="section-gradient-light pt-28">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="grid overflow-hidden rounded-[36px] border border-purple-100 bg-white shadow-[0_30px_100px_rgba(123,31,162,0.1)] lg:grid-cols-[1fr_0.95fr]">
            <div className="bg-gray-900 p-10 text-white sm:p-14">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-purple-200">
                Protected admin access
              </div>
              <h1 className="mt-6 font-heading text-5xl font-black leading-tight">
                Sign in to manage the newsletter operation.
              </h1>
              <p className="mt-6 max-w-lg text-lg leading-8 text-gray-300">
                Use your admin account to access the dashboard, publish newsletters, manage subscribers, and control outgoing email.
              </p>
            </div>

            <motion.form
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              onSubmit={handleSubmit}
              className="p-10 sm:p-14"
            >
              <div className="text-sm uppercase tracking-[0.3em] text-purple-700">Admin login</div>
              <h2 className="mt-3 font-heading text-4xl font-black text-gray-900">Welcome back</h2>
              <div className="mt-10 space-y-5">
                <label className="block">
                  <span className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <Mail className="h-4 w-4 text-purple-700" />
                    Email
                  </span>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                    autoComplete="username"
                    className="w-full rounded-2xl border border-purple-100 px-4 py-4 outline-none transition focus:border-purple-300"
                  />
                </label>
                <label className="block">
                  <span className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <LockKeyhole className="h-4 w-4 text-purple-700" />
                    Password
                  </span>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      value={form.password}
                      onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
                      autoComplete="current-password"
                      className="w-full rounded-2xl border border-purple-100 px-4 py-4 pr-14 outline-none transition focus:border-purple-300"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((current) => !current)}
                      className="absolute inset-y-0 right-0 inline-flex items-center px-4 text-gray-500 transition hover:text-purple-700"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  className="primary-btn w-full rounded-2xl px-5 py-4 text-sm font-bold text-white disabled:opacity-70"
                >
                  {submitting ? "Signing in..." : "Sign in to admin"}
                </button>
                {statusMessage ? <p className="text-sm text-gray-600">{statusMessage}</p> : null}
              </div>
            </motion.form>
          </div>
        </div>
      </section>

      <Footer onContactClick={() => window.location.assign("/")} />
    </div>
  );
};

export default AdminLoginPage;
